import { useState } from 'react';
import { X, AlertCircle, MapPin } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface Address {
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
}

interface Props {
  orderId: string;
  orderType?: 'order' | 'bulk_order';
  initial?: Address | null;
  // Pharmacy state hints — when we know the order is at the pharmacy and the
  // edit will hit the pull-live path, we surface the force-flag UI to admins.
  hasPharmacyOrder?: boolean;
  isAdmin?: boolean;
  // Display labels for context (clinic vs patient ship)
  destination?: 'patient' | 'clinic';
  onClose: () => void;
  onSuccess: () => void;
}

export function EditAddressModal({
  orderId,
  orderType = 'order',
  initial,
  hasPharmacyOrder,
  isAdmin,
  destination,
  onClose,
  onSuccess
}: Props) {
  const [street, setStreet] = useState(initial?.street || '');
  const [street2, setStreet2] = useState(initial?.street2 || '');
  const [city, setCity] = useState(initial?.city || '');
  const [state, setState] = useState(initial?.state || '');
  const [zipCode, setZipCode] = useState(initial?.zipCode || '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresForce, setRequiresForce] = useState(false);
  const [forceApproved, setForceApproved] = useState(false);

  const dirty =
    street.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0 &&
    zipCode.trim().length > 0 &&
    reason.trim().length >= 5;

  const handleSubmit = async () => {
    if (!dirty) return;
    setSubmitting(true);
    setError(null);

    const payload = {
      address: {
        street: street.trim(),
        street2: street2.trim() || undefined,
        city: city.trim(),
        state: state.trim(),
        zipCode: zipCode.trim(),
        force: forceApproved
      },
      reason: reason.trim()
    };

    try {
      const res = orderType === 'bulk_order'
        ? await apiClient.editBulkOrderAddress(orderId, {
          address: {
            street: payload.address.street,
            city: payload.address.city,
            state: payload.address.state,
            zipCode: payload.address.zipCode
          },
          reason: payload.reason
        })
        : await apiClient.editOrderAddress(orderId, payload);

      if (res.success) {
        if ((res as any).pendingPharmacyReview) {
          toast.success('Address change sent to pharmacy for review.');
        } else if ((res as any).requiresManualPharmacySync) {
          toast.success('Address updated. Coordinate the pharmacy sync manually.');
        } else {
          toast.success(res.message || 'Address updated.');
        }
        onSuccess();
        onClose();
      } else if (res.code === 'requires_force') {
        // Admin's first attempt without force; offer the force toggle.
        setRequiresForce(true);
        setError(res.error || 'Pharmacy has created the shipping label. Confirm with force to file a pull-live request.');
      } else if (res.code === 'requires_admin') {
        setError(res.error || 'Only an admin can change the address at this stage.');
      } else if (res.code === 'pending_address_change') {
        setError(res.error || 'A previous address change is still pending pharmacy review.');
      } else {
        setError(res.error || 'Address change failed.');
      }
    } catch (err: any) {
      setError(err?.message || 'Address change failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]"
      onClick={onClose}
    >
      <div
        className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Edit shipping address
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {destination === 'patient' && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-900">
            This is a patient-ship order. The address will be updated on the patient's record and applies to all future orders to this patient.
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 flex gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {requiresForce && isAdmin && !forceApproved && (
          <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-4">
            <div className="text-sm text-amber-900 mb-2">
              Shipping label has been created. Confirming will file a pull-live request — a pharmacist will manually pull the package, archive the old label, and apply the new address.
            </div>
            <button
              onClick={() => setForceApproved(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
            >
              Confirm force + file pull-live
            </button>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
            <input
              type="text"
              value={street}
              onChange={e => setStreet(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {orderType === 'order' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apt / Suite (max 35 chars)</label>
              <input
                type="text"
                maxLength={35}
                value={street2}
                onChange={e => setStreet2(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={e => setCity(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State (2-letter)</label>
              <input
                type="text"
                maxLength={2}
                value={state}
                onChange={e => setState(e.target.value.toUpperCase())}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP code</label>
            <input
              type="text"
              value={zipCode}
              onChange={e => setZipCode(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              minLength={5}
              maxLength={500}
              placeholder="Why is the address being changed?"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {hasPharmacyOrder && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
              Pharmacy already has this order. We'll attempt to push the change to Terminal automatically.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!dirty || submitting || (requiresForce && !forceApproved)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : 'Update address'}
          </button>
        </div>
      </div>
    </div>
  );
}
